import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, BarChart3, Award, Briefcase, MessageCircle, ShoppingBag, Calendar, ChevronDown, CheckCircle2, Star, TrendingUp, Eye, FileCheck, Leaf, Heart } from 'lucide-react';

const PROD_URL = window.location.origin;

const FadeInSection = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
);

const BenefitCard = ({ icon: Icon, title, items, accent }) => (
  <div className={`bg-white rounded-2xl shadow-lg border-t-4 ${accent} p-6 hover:shadow-xl transition-shadow duration-300`}>
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent.replace('border-', 'bg-').replace('-600', '-50').replace('-700', '-50')}`}>
        <Icon className={`w-6 h-6 ${accent.replace('border-', 'text-')}`} />
      </div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FeatureShowcase = ({ icon: Icon, title, description, reversed }) => (
  <div className={`flex flex-col ${reversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 py-8`}>
    <div className="flex-1 text-center md:text-left">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full mb-4">
        <Icon className="w-5 h-5 text-green-700" />
        <span className="text-green-800 font-semibold text-sm">{title}</span>
      </div>
      <p className="text-gray-600 text-base leading-relaxed">{description}</p>
    </div>
  </div>
);

const ConviteTour = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-950 via-green-900 to-emerald-800 text-white">
      {/* Hero - O Convite */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Decorative line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent to-amber-400/50" />

        <FadeInSection className="text-center max-w-3xl z-10">
          {/* Ornamental top */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-16 h-px bg-amber-400/60" />
            <Leaf className="w-6 h-6 text-amber-400" />
            <div className="w-16 h-px bg-amber-400/60" />
          </div>

          <p className="text-amber-300 tracking-[0.3em] uppercase text-xs font-medium mb-4">
            Voce esta convidado
          </p>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4 leading-tight">
            <span className="text-white">Hevea</span>
            <span className="text-emerald-300">Talent</span>
          </h1>

          <div className="w-24 h-0.5 bg-amber-400 mx-auto mb-6" />

          <p className="text-lg sm:text-xl text-green-100 leading-relaxed mb-2">
            A primeira plataforma dedicada a
          </p>
          <p className="text-2xl sm:text-3xl font-semibold text-amber-200 mb-8">
            valorizar quem faz a heveicultura acontecer
          </p>

          <p className="text-green-200/80 text-base max-w-xl mx-auto mb-8 leading-relaxed">
            Conectamos profissionais, proprietarios, tecnicos e toda a cadeia produtiva
            da borracha natural em um unico lugar. Com justica, seguranca e simplicidade.
          </p>

          <p className="text-green-300/40 text-sm mb-4">Role para baixo e conheca</p>
          <div className="flex flex-col items-center gap-1 animate-bounce">
            <ChevronDown className="w-6 h-6 text-amber-400/70" />
            <ChevronDown className="w-6 h-6 text-amber-400/40 -mt-3" />
          </div>
        </FadeInSection>
      </section>

      {/* Seção: Para quem é */}
      <section className="bg-white text-gray-900 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <FadeInSection className="text-center mb-16">
            <p className="text-green-700 tracking-widest uppercase text-xs font-semibold mb-2">Para toda a cadeia</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Cada perfil tem seu lugar
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Independente do seu papel na heveicultura, a HeveaTalent foi pensada para voce.
            </p>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FadeInSection delay={0}>
              <BenefitCard
                icon={Award}
                title="Profissional / Sangrador"
                accent="border-green-600"
                items={[
                  'Selo de qualidade que comprova sua competencia',
                  'Visibilidade no Balcao de Empregos para proprietarios',
                  'Acompanhe seu desempenho no Calendario de Sangria',
                  'Receba avaliacoes de tecnicos (peso 3x) e empregadores (peso 1x)',
                  'Perfil publico com selo Bronze, Prata ou Ouro',
                  'Envie pedidos de recomendacao para fortalecer seu curriculo'
                ]}
              />
            </FadeInSection>

            <FadeInSection delay={100}>
              <BenefitCard
                icon={Briefcase}
                title="Proprietario / Empregador"
                accent="border-amber-600"
                items={[
                  'Encontre profissionais certificados no Balcao de Empregos',
                  'Contrate com confianca: selos garantem qualidade comprovada',
                  'Gerencie sua equipe e propriedades em um so lugar',
                  'Calendario de Sangria com controle de producao diaria',
                  'Marketplace para compra e venda de insumos',
                  'Avalie seus trabalhadores e contribua para o setor'
                ]}
              />
            </FadeInSection>

            <FadeInSection delay={200}>
              <BenefitCard
                icon={FileCheck}
                title="Tecnico / Agronomo"
                accent="border-blue-600"
                items={[
                  'Avaliacoes tecnicas com peso 3x — maior credibilidade',
                  'Controle sobre produtividade alcancada por propriedade',
                  'Dados que auxiliam na tomada de decisoes tecnicas',
                  'Maior destaque profissional por avaliacoes bem fundamentadas',
                  'Acompanhe a evolucao dos profissionais que voce avalia',
                  'Baseado em 14 criterios tecnicos oficiais (INCAPER, CATI/SP)'
                ]}
              />
            </FadeInSection>

            <FadeInSection delay={100}>
              <BenefitCard
                icon={Eye}
                title="Gerente / Fiscal"
                accent="border-purple-600"
                items={[
                  'Monitoramento de equipe com dados de producao reais',
                  'Visao clara do desempenho de cada trabalhador',
                  'Metas contextuais justas baseadas em clone, sistema e regiao',
                  'Historico completo de sangrias e registros diarios',
                  'Facilite a comunicacao dentro da cadeia produtiva'
                ]}
              />
            </FadeInSection>

            <FadeInSection delay={200}>
              <BenefitCard
                icon={Users}
                title="Comunidade"
                accent="border-teal-600"
                items={[
                  'Compartilhe dicas e experiencias com outros profissionais',
                  'Aprenda com quem vive a heveicultura no dia a dia',
                  'Espaco colaborativo para troca de conhecimento',
                  'Fortaleca a cadeia produtiva com informacao de qualidade'
                ]}
              />
            </FadeInSection>

            <FadeInSection delay={300}>
              <BenefitCard
                icon={ShoppingBag}
                title="Marketplace"
                accent="border-orange-600"
                items={[
                  'Compre e venda insumos, equipamentos e servicos',
                  'Conecte-se diretamente com fornecedores do setor',
                  'Facilite a logistica da sua operacao',
                  'Visibilidade para produtos e servicos da heveicultura'
                ]}
              />
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Seção: Como funciona o selo */}
      <section className="bg-green-950 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInSection className="text-center mb-12">
            <p className="text-amber-400 tracking-widest uppercase text-xs font-semibold mb-2">Transparencia</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Como funciona a avaliacao
            </h2>
            <p className="text-green-200/70 max-w-xl mx-auto">
              Um sistema justo, baseado em ciencia e na realidade do campo.
            </p>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <FadeInSection>
              <div className="bg-green-900/50 border border-green-700/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Avaliacao Tecnica</h3>
                  <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">peso 3x</span>
                </div>
                <p className="text-green-200/70 text-sm leading-relaxed">
                  Realizada por Engenheiros Agronomos ou Tecnicos Agricolas certificados.
                  Baseada em 14 criterios tecnicos oficiais. Maior credibilidade e peso no selo final.
                </p>
              </div>
            </FadeInSection>

            <FadeInSection delay={100}>
              <div className="bg-green-900/50 border border-green-700/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Avaliacao de Empregador</h3>
                  <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-1 rounded-full">peso 1x</span>
                </div>
                <p className="text-green-200/70 text-sm leading-relaxed">
                  Realizada por proprietarios que convivem com o profissional no dia a dia.
                  Reflete a experiencia real de trabalho, comprometimento e confiabilidade.
                </p>
              </div>
            </FadeInSection>
          </div>

          {/* Dois caminhos */}
          <FadeInSection>
            <div className="bg-green-900/30 border border-green-700/20 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-6">Dois caminhos para o selo</h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <div className="bg-green-800/50 rounded-xl p-5 flex-1 max-w-xs">
                  <div className="text-3xl font-bold text-emerald-300 mb-2">50 / 50</div>
                  <p className="text-green-200/70 text-sm">
                    50% avaliacao tecnica + 50% desempenho contextual (com dados do Calendario)
                  </p>
                </div>
                <div className="text-green-500 font-bold text-lg">ou</div>
                <div className="bg-green-800/50 rounded-xl p-5 flex-1 max-w-xs">
                  <div className="text-3xl font-bold text-blue-300 mb-2">100%</div>
                  <p className="text-green-200/70 text-sm">
                    100% avaliacao tecnica (quando nao ha dados no Calendario de Sangria)
                  </p>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Selos */}
          <FadeInSection className="mt-8">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-700/30 border-2 border-amber-600 flex items-center justify-center mx-auto mb-2">
                  <Award className="w-8 h-8 text-amber-500" />
                </div>
                <p className="text-amber-400 font-bold text-sm">Bronze</p>
                <p className="text-green-300/50 text-xs">60-74 pts</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gray-400/20 border-2 border-gray-300 flex items-center justify-center mx-auto mb-2">
                  <Award className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-300 font-bold text-sm">Prata</p>
                <p className="text-green-300/50 text-xs">75-89 pts</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-400 flex items-center justify-center mx-auto mb-2">
                  <Award className="w-8 h-8 text-yellow-400" />
                </div>
                <p className="text-yellow-400 font-bold text-sm">Ouro</p>
                <p className="text-green-300/50 text-xs">90-100 pts</p>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Seção: Recursos da plataforma */}
      <section className="bg-white text-gray-900 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInSection className="text-center mb-12">
            <p className="text-green-700 tracking-widest uppercase text-xs font-semibold mb-2">Recursos</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tudo o que voce precisa, em um so lugar
            </h2>
          </FadeInSection>

          <div className="space-y-2">
            <FadeInSection>
              <FeatureShowcase
                icon={Calendar}
                title="Calendario de Sangria"
                description="Registre sua producao diaria, acompanhe metas contextuais baseadas no seu clone, sistema de sangria e regiao. Veja sua evolucao ao longo do tempo com graficos claros. Controle de Ethrel, coletas e aplicacoes em um so lugar."
              />
            </FadeInSection>

            <FadeInSection>
              <FeatureShowcase
                icon={Award}
                title="Selo Profissional"
                description="Ao aceitar o termo LGPD, seu perfil fica visivel para empregadores no Balcao de Empregos. Seu selo (Bronze, Prata ou Ouro) aparece no seu perfil publico, comprovando sua competencia para toda a cadeia. O selo e cumulativo — quanto mais avaliacoes, mais confiavel."
                reversed
              />
            </FadeInSection>

            <FadeInSection>
              <FeatureShowcase
                icon={Briefcase}
                title="Central de Empregos"
                description="Proprietarios publicam vagas, profissionais buscam oportunidades. Tudo com comunicacao direta pelo chat da plataforma. Ofereca seus servicos ou encontre a mao de obra qualificada que precisa."
              />
            </FadeInSection>

            <FadeInSection>
              <FeatureShowcase
                icon={TrendingUp}
                title="Metas Contextuais"
                description="Nada de metas genericas. O sistema calcula metas justas baseadas no clone plantado, no sistema de sangria utilizado e na regiao da propriedade. Se voce tem multiplos clones, a meta e ponderada pela area de cada um."
                reversed
              />
            </FadeInSection>

            <FadeInSection>
              <FeatureShowcase
                icon={MessageCircle}
                title="Comunidade"
                description="Compartilhe dicas, tire duvidas e aprenda com outros profissionais. Um espaco colaborativo onde o conhecimento pratico do campo e valorizado. Anexe fotos e interaja com comentarios."
              />
            </FadeInSection>

            <FadeInSection>
              <FeatureShowcase
                icon={ShoppingBag}
                title="Marketplace"
                description="Compre e venda insumos, equipamentos e servicos diretamente na plataforma. Facilite a logistica da sua operacao conectando-se com fornecedores e compradores do setor."
                reversed
              />
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Seção: Tripé */}
      <section className="bg-green-900 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInSection className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Nosso Tripe</h2>
            <p className="text-green-200/60 text-sm">Os pilares que sustentam cada decisao na plataforma</p>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <FadeInSection>
              <div className="text-center p-6">
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Justica</h3>
                <p className="text-green-200/60 text-sm">
                  Avaliacoes baseadas em criterios tecnicos oficiais. Metas calculadas pela realidade de cada propriedade.
                </p>
              </div>
            </FadeInSection>

            <FadeInSection delay={100}>
              <div className="text-center p-6">
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Seguranca</h3>
                <p className="text-green-200/60 text-sm">
                  Dados protegidos pela LGPD. Avaliacoes privadas. Voce decide o que compartilhar no seu perfil publico.
                </p>
              </div>
            </FadeInSection>

            <FadeInSection delay={200}>
              <div className="text-center p-6">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Simplicidade</h3>
                <p className="text-green-200/60 text-sm">
                  Interface intuitiva. Poucos cliques para registrar, avaliar ou contratar. Feito para o campo, nao para escritorios.
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Seção: Comece Grátis */}
      <section className="bg-emerald-900 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInSection className="text-center mb-10">
            <p className="text-amber-400 tracking-widest uppercase text-xs font-semibold mb-2">Sem compromisso</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Comece gratis. Evolua no seu ritmo.
            </h2>
          </FadeInSection>

          <FadeInSection>
            <div className="bg-emerald-800/50 border border-emerald-600/30 rounded-2xl p-8 text-center mb-8">
              <div className="text-5xl font-bold text-amber-400 mb-2">45 dias</div>
              <p className="text-xl text-white font-semibold mb-3">de acesso completo e gratuito</p>
              <p className="text-emerald-200/70 max-w-lg mx-auto leading-relaxed">
                Ao se cadastrar, voce recebe automaticamente 45 dias gratis com acesso total
                ao plano mais completo do seu perfil. Sem cartao de credito. Sem pegadinha.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={100}>
            <div className="bg-emerald-800/30 border border-emerald-700/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white text-center mb-6">Depois dos 45 dias, voce escolhe:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-emerald-900/50 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-emerald-300 mb-1">Gratuito</div>
                  <p className="text-emerald-200/60 text-sm">Continue com os recursos basicos, sem custo algum</p>
                </div>
                <div className="bg-emerald-900/50 rounded-xl p-5 text-center border border-amber-500/30">
                  <div className="text-2xl font-bold text-amber-300 mb-1">Essencial</div>
                  <p className="text-emerald-200/60 text-sm">Recursos intermediarios para quem quer crescer</p>
                </div>
                <div className="bg-emerald-900/50 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-blue-300 mb-1">Completo</div>
                  <p className="text-emerald-200/60 text-sm">Tudo liberado para quem quer o maximo da plataforma</p>
                </div>
              </div>
              <p className="text-emerald-300/50 text-center text-sm mt-4">
                Migre para o plano que corresponda as suas expectativas. Sem pressao, no seu tempo.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-gradient-to-b from-green-950 to-black py-24 px-4 text-center">
        <FadeInSection>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-16 h-px bg-amber-400/40" />
              <Leaf className="w-5 h-5 text-amber-400/60" />
              <div className="w-16 h-px bg-amber-400/40" />
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Faca parte desta historia
            </h2>
            <p className="text-green-200/70 mb-10 text-lg">
              A heveicultura merece uma plataforma a sua altura.
              Cadastre-se gratuitamente e comece hoje.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href={`${PROD_URL}/register`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-amber-500 hover:bg-amber-400 text-green-950 font-bold rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5"
                data-testid="cta-register-bottom"
              >
                Comecar Gratis
              </a>
              <a
                href={PROD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 border-2 border-green-500/30 hover:border-amber-400 text-green-200 hover:text-amber-200 font-semibold rounded-full text-lg transition-all duration-300"
              >
                Visitar Plataforma
              </a>
            </div>

            <p className="text-green-500/40 text-xs mt-12">
              HeveaTalent &mdash; Justica, Seguranca, Simplicidade
            </p>
          </div>
        </FadeInSection>
      </section>
    </div>
  );
};

export default ConviteTour;
